import type Phaser from "phaser";
import type { GameApi, GameApiRegistry } from "../app/GameApi.js";
import type { GameContext } from "../app/GameContext.js";
import type { ShellSystems } from "../app/createShellSystems.js";
import type { FeatureFlags } from "../env/featureFlags.js";
import { formatSpecSummary } from "../ui/specSummary.js";
import { ShellFrame } from "../ui/ShellFrame.js";
import { GuideQuestHighlighter } from "../ui/GuideQuestHighlighter.js";
import {
  resolveGuideQuestHighlightRects,
  resolveVisibleGuideQuestHighlightTarget,
} from "../ui/resolveGuideQuestHighlightRect.js";
import { tabHelpScenarioId } from "../tutorial/tabHelpScenario.js";
import type { TabRouter } from "../app/TabRouter.js";
import type { TabRefresher } from "../ui/TabRefresher.js";
import type { ToastPresenter } from "../ui/ToastPresenter.js";
import type { ModalHost } from "../ui/ModalHost.js";
import type { SceneReadiness } from "./SceneReadiness.js";
import type { DisposerBag } from "@yjsgames/boot";
import { createPerfTool, type PerfTool } from "../diag/perfOverlay.js";
import type { ShellLayout } from "../data/balance.js";
import type { Rect } from "@yjsgames/phaser-ui/panel-content";
import type { ProgressState } from "../app/saveSwap.js";
import type { AutoPlaySnapshot } from "../app/buildAutoPlaySnapshot.js";
import type { DailyResetService } from "@yjsgames/daily-reset";
import type { CooldownService } from "@yjsgames/cooldown";
import { countPhaserCanvas, countPhaserDisplayList, type TargetFpsSetting } from "@yjsgames/perf";
import { PERF_OVERLAY_AVAILABLE } from "../models/BrickSettings.js";
import { FORCE_LOW_SPEC_RENDER_ENABLED, PERF_RENDERABLE_BUCKETS } from "../env/appFlavor.js";
import { runGameSceneBoot } from "./gameSceneBoot.js";
import { resolveGuideQuestHighlightTarget } from "@yjsgames/guide-quest";
import type { BattlefieldRunner } from "./BattlefieldRunner.js";
import type { DungeonFlow } from "./DungeonFlow.js";
import type { AccountLinkProvider } from "@yjsgames/account-link";
import type { BattleProcEvent } from "../ui/ModuleGridView.js";
import type { TabId } from "../ui/tabVisibility.js";
import type { BattleDialogueData } from "../ui/battleDialogueTypes.js";
import { t } from "../i18n/i18n.js";
import {
  dialogueIdsForTrigger,
  dialogueLinesById,
  type DialogueLine,
  type DialogueTrigger,
} from "../data/dialogueData.js";
import { createDialogueGameplayPauseController } from "./dialogueGameplayPause.js";

const accountNeedsLink = (provider: AccountLinkProvider | null | undefined): boolean =>
  provider?.isSupported() === true && provider.getStatus().linked === false;

export interface GameSceneUiRuntimeDeps {
  readonly scene: Phaser.Scene;
  readonly ctx: GameContext;
  readonly shell: ShellSystems;
  readonly features: FeatureFlags;
  readonly layout: ShellLayout;
  readonly progress: () => ProgressState;
  readonly apiRegistry: GameApiRegistry;
  readonly disposers: DisposerBag;
  readonly modals: ModalHost;
  readonly tabs: TabRefresher;
  readonly tabRouter: TabRouter;
  readonly toasts: ToastPresenter;
  readonly readiness: SceneReadiness;
  readonly runner: () => BattlefieldRunner;
  readonly dungeonFlow: () => DungeonFlow;
  readonly dailyReset: () => DailyResetService;
  readonly cooldown: () => CooldownService;
  readonly registryGet: (key: string) => unknown;
  readonly getAutoPlaySnapshot: () => AutoPlaySnapshot;
  readonly settingsToggle: () => void;
  readonly boxResult: () => string;
  readonly registerDailyReset: (key: string, onReset: () => void) => void;
  readonly resetSave: () => void;
  readonly startTutorial: (scenarioId?: string) => void;
  readonly resetTutorial: (scenarioId?: string) => void;
  readonly toggleGameviewExpand: () => void;
  readonly togglePerfOverlay: () => boolean;
  readonly setBattleProc: (fn: (procs: readonly BattleProcEvent[], startFired: boolean) => void) => void;
  readonly setSettingsToggle: (fn: () => void) => void;
  readonly applyTargetFps: (setting: TargetFpsSetting) => void;
  readonly cheatForceHouseAd: () => boolean;
  readonly tutorialGate: (key: string) => boolean;
  readonly readyEvent: string;
  readonly provideForceDialogueTriggerHandler: (handler: (trigger: DialogueTrigger) => void) => void;
}

export interface GameSceneUiRuntime {
  readonly frame: ShellFrame;
  readonly api: GameApi;
  readonly perf: PerfTool;
}

/** ShellFrame + domain boot + perf overlay 조립. */
export function buildGameSceneUiRuntime(deps: GameSceneUiRuntimeDeps): GameSceneUiRuntime {
  let perf: PerfTool | undefined;
  let frameRef: ShellFrame | null = null;
  let statUpgradeActionRectProvider: (() => Rect | null) | null = null;
  let shopGoldBoxRectProvider: (() => Rect | null) | null = null;
  let forcedBattleDialogue: BattleDialogueData | null = null;
  const dialogueGameplayPause = createDialogueGameplayPauseController([
    { setPaused: (paused) => deps.runner().setPaused(paused) },
    { setPaused: (paused) => deps.dungeonFlow().setPaused(paused) },
  ]);
  deps.disposers.add(() => dialogueGameplayPause.resume());
  const markDialogueSeen = (dialogueId: string): void => {
    const seen = deps.progress().dialogueSeenIds;
    if (!seen.includes(dialogueId)) seen.push(dialogueId);
    deps.shell.persistScheduler.request();
  };
  const guideQuestHighlighter = new GuideQuestHighlighter(deps.scene);
  deps.disposers.add(() => guideQuestHighlighter.dispose());
  const setActiveTab = (tab: TabId) => {
    guideQuestHighlighter.clear();
    deps.tabRouter.setActive(tab);
  };
  const focusGuideQuestTarget = () => {
    const active = deps.ctx.guideQuest().active();
    if (!active || active.completed) return;
    const target = resolveGuideQuestHighlightTarget(active.definition);
    const visibleTarget = resolveVisibleGuideQuestHighlightTarget(target, deps.tabRouter.current());
    const frame = frameRef
      ? {
          tabContent: frameRef.tabContent,
          hud: frameRef.hud,
          bottomTab: frameRef.bottomTab,
          guideQuestPanel: frameRef.guideQuestPanel,
          statUpgrade: {
            upgradeButtonRect: () => statUpgradeActionRectProvider?.() ?? null,
          },
          shop: {
            goldBoxButtonRect: () => shopGoldBoxRectProvider?.() ?? null,
          },
        }
      : null;
    const rects = frame ? resolveGuideQuestHighlightRects(frame, visibleTarget) : [];
    if (rects.length > 0) guideQuestHighlighter.highlightMany(rects);
  };
    const frame = new ShellFrame(deps.scene, {
      onSettingsClick: deps.settingsToggle,
      accountNeedsLink: accountNeedsLink(deps.registryGet("accountLink") as AccountLinkProvider | undefined),
    onTabSelected: setActiveTab,
    enabledTabs: deps.tabRouter.enabled,
    layout: deps.layout,
    onToggleExpand: deps.toggleGameviewExpand,
    onHelp: () => deps.startTutorial(tabHelpScenarioId(deps.tabRouter.current())),
    onGuideQuestClaim: () => deps.shell.guideQuest.claimCurrentReward(),
    onGuideQuestGuide: focusGuideQuestTarget,
    onBattleDialogueComplete: (dialogueId) => {
      if (forcedBattleDialogue?.dialogueId === dialogueId) forcedBattleDialogue = null;
      dialogueGameplayPause.complete(dialogueId);
      markDialogueSeen(dialogueId);
    },
  });
  frameRef = frame;
  frame.setLowSpecRenderMode(FORCE_LOW_SPEC_RENDER_ENABLED);
  const tabContent = frame.tabContent;

  runGameSceneBoot({
    scene: deps.scene,
    ctx: deps.ctx,
    tabs: deps.tabs,
    apiRegister: (slice) => deps.apiRegistry.register(slice),
    addDisposer: (fn) => deps.disposers.add(fn),
    modals: deps.modals,
    screen: () => ({ width: deps.scene.scale.width, height: deps.scene.scale.height }),
    openTab: setActiveTab,
    settings: deps.shell.settings,
    cheats: () => deps.shell.cheats,
    cheatForceHouseAd: deps.cheatForceHouseAd,
    togglePerfOverlay: deps.togglePerfOverlay,
    perfOverlayEnabled: () => perf?.isEnabled() ?? false,
    rewardedAd: () => deps.shell.rewardedAd,
    snapshot: deps.getAutoPlaySnapshot,
    ui: { showToast: (m) => deps.toasts.showToast(m) },
    battleDialogue: {
      update: (data) => frame.updateBattleDialogue(forcedBattleDialogue ?? data),
      force: (data) => {
        forcedBattleDialogue = data;
        if (data.pauseGameplay === true) dialogueGameplayPause.pause(data.dialogueId);
        frame.setBattleDialogueVisible(true);
        try {
          frame.playBattleDialogue(data);
        } catch (error) {
          if (data.pauseGameplay === true) dialogueGameplayPause.complete(data.dialogueId);
          throw error;
        }
      },
      setVisible: (visible) => frame.setBattleDialogueVisible(visible),
      applyLanguage: () => frame.applyBattleDialogueLanguage(),
    },
    commands: {
      grid: deps.shell.grid,
      economy: deps.shell.economy,
      dailyShop: deps.shell.dailyShop,
      shopAd: deps.shell.shopAd,
      iap: deps.shell.iap,
    },
    guideQuest: deps.shell.guideQuest,
    guideQuestHighlight: {
      setStatUpgradeActionRectProvider: (provider) => {
        statUpgradeActionRectProvider = provider;
      },
      setShopGoldBoxRectProvider: (provider) => {
        shopGoldBoxRectProvider = provider;
      },
    },
    dailyReset: deps.dailyReset,
    cooldown: deps.cooldown,
    registerDailyReset: deps.registerDailyReset,
    dungeon: {
      challenge: () => deps.dungeonFlow().challenge(),
      sweep: () => deps.dungeonFlow().sweep(),
      isActive: () => deps.dungeonFlow().isActive(),
    },
    resetSave: deps.resetSave,
    registryGet: deps.registryGet,
    combatSpecSummary: () => formatSpecSummary(deps.shell.combat.buildSpec()),
    provideBattleProc: deps.setBattleProc,
    tutorial: {
      start: deps.startTutorial,
      reset: deps.resetTutorial,
    },
    tutorialGate: deps.tutorialGate,
    provideSettingsToggle: deps.setSettingsToggle,
    setAccountNeedsLink: (value) => {
      frame.setAccountNeedsLink(value);
      deps.modals.setSettingsAccountNeedsLink(value);
    },
    tabContent: {
      x: tabContent.x,
      y: tabContent.y,
      width: tabContent.width,
      height: tabContent.height,
    },
    shellLayout: deps.layout,
    ports: deps.shell.ports,
    rewards: deps.shell.rewards,
    features: deps.features,
    state: {
      discovered: () => deps.progress().discovered,
      adFree: () => deps.progress().adFree,
      boxResult: deps.boxResult,
      codexSeenChapter: () => deps.progress().codexSeenChapter,
      shopDailyAdDiamondClaimed: () => deps.progress().shopDailyAdDiamondClaimed,
      dialogueSeenIds: () => deps.progress().dialogueSeenIds,
      markDialogueSeen,
    },
    progression: deps.shell.progression,
  });
  deps.provideForceDialogueTriggerHandler((trigger) => {
    const data = forceBattleDialogueData(trigger, deps.progress().dialogueSeenIds);
    if (!data) return;
    forcedBattleDialogue = data;
    if (data.pauseGameplay === true) dialogueGameplayPause.pause(data.dialogueId);
    frame.setBattleDialogueVisible(true);
    try {
      frame.playBattleDialogue(data);
    } catch (error) {
      if (data.pauseGameplay === true) dialogueGameplayPause.complete(data.dialogueId);
      throw error;
    }
  });

  perf = createPerfTool(
    deps.scene,
    deps.scene.game,
    () => ({
      proj: deps.runner().countActiveProjectiles(),
      poolCreated: deps.runner().projectilePoolStats().created,
      poolReused: deps.runner().projectilePoolStats().reused,
      poolIdle: deps.runner().projectilePoolStats().idle,
      brick: deps.runner().countActiveBricks(),
      tween: deps.scene.tweens.getTweens().length,
      ...displayCounts(deps.scene.children.list),
      ...countPhaserCanvas(deps.scene.game),
    }),
    PERF_OVERLAY_AVAILABLE && deps.shell.settings.perfOverlayEnabled,
  );
  deps.disposers.add(() => perf?.destroy());
  deps.applyTargetFps(deps.shell.settings.targetFps);
  const api = deps.apiRegistry.build();
  deps.readiness.markReady();
  deps.scene.events.emit(deps.readyEvent);
  return { frame, api, perf };
}

function displayCounts(list: Phaser.GameObjects.GameObject[]): Record<string, number> {
  return countPhaserDisplayList(list, {
    depthBucketSize: 10,
    maxDepthBuckets: 4,
    maxHiddenActiveBuckets: 8,
    maxRenderableBuckets: PERF_RENDERABLE_BUCKETS,
  });
}

function forceBattleDialogueData(
  trigger: DialogueTrigger,
  seenIds: readonly string[],
): BattleDialogueData | null {
  const seen = new Set(seenIds);
  for (const dialogueId of dialogueIdsForTrigger(trigger, "battle", undefined, "force")) {
    const lines = dialogueLinesById(dialogueId);
    if (lines.length === 0) continue;
    if (isOnceDialogue(lines) && seen.has(dialogueId)) continue;
    return {
      dialogueId,
      unread: !seen.has(dialogueId),
      pauseGameplay: trigger === "w1BossDefeated",
      lines: lines.map((line) => ({
        speaker: t(line.speakerKey),
        text: t(line.textKey),
      })),
    };
  }
  return null;
}

function isOnceDialogue(lines: readonly DialogueLine[]): boolean {
  return lines.some((line) => line.once);
}
